import { useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useEnterpriseTour } from "./EnterpriseTourContext";
import { useAppConfig } from "../../appConfig";
import SpotlightOverlay from "./SpotlightOverlay";
import CutSceneTransition from "./CutSceneTransition";
import TourProgressBar from "./TourProgressBar";
import { TOUR_TIMING } from "./constants";

const EnterpriseTour = () => {
  const navigate = useNavigate();
  const appConfig = useAppConfig();
  const tour = useEnterpriseTour();
  const actionTimerRef = useRef(null);

  const {
    isActive,
    isPaused,
    showCutScene,
    isNavigating,
    sectionIndex,
    currentSection,
    currentStep,
    progress,
    actions,
  } = tour;

  // Register navigate function with context
  useEffect(() => {
    actions.setNavigateFn(navigate);
  }, [navigate, actions]);

  // Execute a named action (DOM click, etc.)
  const executeAction = useCallback((actionName) => {
    if (actionName === "clickMatchesTab") {
      const tab = document.querySelector('.tab-wrap .nav-item:last-child .nav-link');
      if (tab) tab.click();
    } else if (actionName === "clickInventoryMatches") {
      const badge = document.querySelector(
        ".medium-blue-tag-bg-border-clickable, .medium-green-tag-bg-border-clickable"
      );
      if (badge) badge.click();
    } else if (actionName === "clickMyWantListing") {
      const cell = document.querySelector("tbody tr:first-child td");
      if (cell) cell.click();
    } else if (actionName === "clickInventoryProperty") {
      const cell = document.querySelector("tbody tr:first-child td");
      if (cell) cell.click();
    } else if (actionName === "clickMatchTabOnFeed") {
      const matchTab = document.querySelector('#controlled-tab-example-tab-match');
      if (matchTab) matchTab.click();
    } else if (actionName === "clickOtherWantListing") {
      const card = document.querySelector(".home-listing-wrapper .property-listing-link:first-child");
      if (card) card.click();
    }
  }, []);

  // Auto-execute step actions (only for steps with `action`, not `nextAction`)
  useEffect(() => {
    if (!isActive || isPaused || showCutScene || isNavigating || !currentStep?.action) return;

    clearTimeout(actionTimerRef.current);
    actionTimerRef.current = setTimeout(() => {
      executeAction(currentStep.action);
    }, TOUR_TIMING.SCROLL_SETTLE_MS);

    return () => clearTimeout(actionTimerRef.current);
  }, [isActive, isPaused, showCutScene, isNavigating, currentStep, executeAction]);

  const handleNext = useCallback(() => {
    // Execute nextAction (fires on user click, not auto) before advancing
    if (currentStep?.nextAction) {
      executeAction(currentStep.nextAction);
      // Give React time to re-render after the action (e.g. tab click)
      // before advancing to the next step that may target new content
      // Was 600ms - the waitFor loop in SpotlightOverlay already polls for
      // target element mount at 200ms intervals, so a long blocking wait
      // here just delays everything. 150ms is enough for React to flush.
      setTimeout(() => actions.next(appConfig), 150);
      return;
    }
    actions.next(appConfig);
  }, [actions, appConfig, currentStep, executeAction]);
  const handleBack = useCallback(() => actions.back(appConfig), [actions, appConfig]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;
    const handler = (e) => {
      if (e.key === "Escape") actions.exitTour();
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        handleNext();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleBack();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isActive, actions, handleNext, handleBack]);

  const handleCutSceneComplete = useCallback(() => {
    actions.next(appConfig);
  }, [actions, appConfig]);

  if (!isActive) return null;

  const cutSceneConfig =
    currentSection?.cutScene?.isFinal && appConfig?.tenant?.tourContactLine
      ? { ...currentSection.cutScene, subline: appConfig.tenant.tourContactLine }
      : currentSection?.cutScene;

  return (
    <>
      {/* Cut scene transitions */}
      {showCutScene && cutSceneConfig && (
        <CutSceneTransition
          config={cutSceneConfig}
          onComplete={handleCutSceneComplete}
        />
      )}

      {/* Spotlight overlay for steps */}
      {!showCutScene && !isNavigating && !isPaused && currentStep && (
        <SpotlightOverlay step={currentStep} />
      )}

      {/* Navigation controls overlay - shown for both steps and cut scenes */}
      {!isNavigating && (currentStep || showCutScene) && (
        <div className={`et-controls ${isPaused ? "et-controls--paused" : ""}`}>
          <div className="et-controls__nav">
            <button className="et-controls__btn et-controls__btn--back" onClick={handleBack}>
              Back
            </button>
            <button
              className="et-controls__btn et-controls__btn--next"
              onClick={showCutScene ? handleCutSceneComplete : handleNext}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Progress bar */}
      {!showCutScene && (
        <TourProgressBar
          sectionIndex={sectionIndex}
          progress={progress}
          isPaused={isPaused}
          onPause={actions.pause}
          onResume={actions.resume}
          onExit={actions.exitTour}
        />
      )}
    </>
  );
};

export default EnterpriseTour;
